import {
  branch,
  cash_movement,
  combo,
  combo_item,
  customer,
  dining_table,
  employee,
  ingredient,
  isNull,
  loyalty_movement,
  menu_category,
  menu_product,
  modifier_group,
  modifier_option,
  order,
  order_line,
  order_line_modifier,
  payment,
  product_modifier_group,
  product_variant,
  promotion,
  purchase_order,
  purchase_order_line,
  recipe_item,
  register_session,
  reservation,
  sql,
  stock_movement,
  supplier,
} from "@malla/powersync";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)                 │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant "live row = `deleted_at IS
 * NULL`" is expressed for the 27 tables with trash/voiding in this POS repo.
 * Every live read composes these predicates (the domain builders in
 * menu/orders/inventory/… and the live layer import them from here). The ONLY
 * reads that see deleted rows are the unified trash
 * (`trash/queries.ts`, with dynamic `.from(<var>)` that the guard does not touch) and
 * harmless id-scoped lookups, declared in the guard whitelist
 * (`guard-manifest.ts`).
 *
 * Why centralize? The scattered invariant was the #1 bug family of the
 * campaign (⌘K search and the checkUnsyncedActor counter ignored `deleted_at`;
 * reports summed lines/payments from voided orders). With a single choke point
 * + anti-regression guard, adding a raw read of these tables outside the
 * whitelist BREAKS the build (see `live/__tests__/live-access-guard.test.ts`).
 *
 * ─── Cohort hierarchy (POS) ────────────────────────────────────────────────
 * A CHILD row is live only if its own `deleted_at IS NULL` AND its trash-capable
 * parent is not in the trash (a `NOT EXISTS` cohort). Verified against the
 * logical FKs in `packages/powersync/src/schema.ts` (there are no physical FKs
 * in the DB: PowerSync models them logically):
 *   - menu_product        → menu_category (category_id)
 *   - product_variant     → menu_product  (product_id)
 *   - product_modifier_group → menu_product (product_id) + modifier_group (pivot)
 *   - modifier_option     → modifier_group (modifier_group_id)
 *   - combo_item          → combo          (combo_id)
 *   - recipe_item         → menu_product   (product_id)  [owner; ingredient is ref]
 *   - order_line          → order          (order_id)
 *   - order_line_modifier → order_line     (order_line_id)  [direct; see note]
 *   - payment             → order          (order_id)
 *   - purchase_order_line → purchase_order (purchase_order_id)
 *   - loyalty_movement    → customer       (customer_id)
 *   - stock_movement      → ingredient     (ingredient_id)
 * The other soft-delete tables are roots (top-level catalog or transactional
 * operational entities: branch, customer, employee, ingredient, menu_category, combo,
 * modifier_group, promotion, supplier, order, register_session, cash_movement,
 * dining_table, reservation, purchase_order) → only their own `deleted_at`.
 *
 * NOTE (one-level cohort): the cohort checks the DIRECT PARENT (the pilot/tendly
 * pattern). For example, `order_line_modifier` looks at its `order_line`, not its
 * grandparent `order` — but in live views the modifiers are only read within their
 * already-live line/order, so the boundary never appears.
 */

/**
 * Generic cohort predicate: the child row hangs from a parent that is NOT in
 * the trash. With parent soft-delete the children remain intact (so the whole
 * cohort can be restored) but disappear from live views.
 */
function parentNotTrashed(
  parent: SQLiteTable & { id: AnySQLiteColumn; deleted_at: AnySQLiteColumn },
  childFk: AnySQLiteColumn,
) {
  return sql`NOT EXISTS (SELECT 1 FROM ${parent} WHERE ${parent.id} = ${childFk} AND ${parent.deleted_at} IS NOT NULL)`;
}

// ─── Roots (only their own deleted_at) ──────────────────────────────────────

export function liveBranchFilters() {
  return [isNull(branch.deleted_at)];
}

export function liveMenuCategoryFilters() {
  return [isNull(menu_category.deleted_at)];
}

export function liveModifierGroupFilters() {
  return [isNull(modifier_group.deleted_at)];
}

export function liveComboFilters() {
  return [isNull(combo.deleted_at)];
}

export function liveIngredientFilters() {
  return [isNull(ingredient.deleted_at)];
}

export function liveCustomerFilters() {
  return [isNull(customer.deleted_at)];
}

export function livePromotionFilters() {
  return [isNull(promotion.deleted_at)];
}

export function liveEmployeeFilters() {
  return [isNull(employee.deleted_at)];
}

export function liveSupplierFilters() {
  return [isNull(supplier.deleted_at)];
}

export function livePurchaseOrderFilters() {
  return [isNull(purchase_order.deleted_at)];
}

export function liveDiningTableFilters() {
  return [isNull(dining_table.deleted_at)];
}

export function liveReservationFilters() {
  return [isNull(reservation.deleted_at)];
}

export function liveRegisterSessionFilters() {
  return [isNull(register_session.deleted_at)];
}

export function liveCashMovementFilters() {
  return [isNull(cash_movement.deleted_at)];
}

export function liveOrderFilters() {
  return [isNull(order.deleted_at)];
}

// ─── Children with cohort (parent with trash not deleted) ───────────────────

export function liveMenuProductFilters() {
  return [
    isNull(menu_product.deleted_at),
    parentNotTrashed(menu_category, menu_product.category_id),
  ];
}

export function liveProductVariantFilters() {
  return [
    isNull(product_variant.deleted_at),
    parentNotTrashed(menu_product, product_variant.product_id),
  ];
}

export function liveProductModifierGroupFilters() {
  return [
    isNull(product_modifier_group.deleted_at),
    parentNotTrashed(menu_product, product_modifier_group.product_id),
    parentNotTrashed(modifier_group, product_modifier_group.modifier_group_id),
  ];
}

export function liveModifierOptionFilters() {
  return [
    isNull(modifier_option.deleted_at),
    parentNotTrashed(modifier_group, modifier_option.modifier_group_id),
  ];
}

export function liveComboItemFilters() {
  return [isNull(combo_item.deleted_at), parentNotTrashed(combo, combo_item.combo_id)];
}

export function liveRecipeItemFilters() {
  return [isNull(recipe_item.deleted_at), parentNotTrashed(menu_product, recipe_item.product_id)];
}

export function liveOrderLineFilters() {
  return [isNull(order_line.deleted_at), parentNotTrashed(order, order_line.order_id)];
}

export function liveOrderLineModifierFilters() {
  return [
    isNull(order_line_modifier.deleted_at),
    parentNotTrashed(order_line, order_line_modifier.order_line_id),
  ];
}

export function livePaymentFilters() {
  return [isNull(payment.deleted_at), parentNotTrashed(order, payment.order_id)];
}

export function livePurchaseOrderLineFilters() {
  return [
    isNull(purchase_order_line.deleted_at),
    parentNotTrashed(purchase_order, purchase_order_line.purchase_order_id),
  ];
}

export function liveLoyaltyMovementFilters() {
  return [
    isNull(loyalty_movement.deleted_at),
    parentNotTrashed(customer, loyalty_movement.customer_id),
  ];
}

export function liveStockMovementFilters() {
  return [
    isNull(stock_movement.deleted_at),
    parentNotTrashed(ingredient, stock_movement.ingredient_id),
  ];
}
